import React, { useEffect, useState } from 'react';
import {
  X, Phone, Mail, MapPin, Calendar, FileText, MessageCircle, Send, Clock, Tag, Globe, Award,
} from 'lucide-react';
import { fetchLeadNotes, addLeadNote } from './api';
import type { UnifiedLead, LeadNote, LeadStatus } from './types';
import { LEAD_STATUSES, STATUS_COLORS } from './types';

interface LeadDetailModalProps {
  lead: UnifiedLead;
  adminId?: number;
  onClose: () => void;
  onStatusChange: (status: LeadStatus) => void;
}

export default function LeadDetailModal({ lead, adminId, onClose, onStatusChange }: LeadDetailModalProps) {
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);

  useEffect(() => {
    setLoadingNotes(true);
    fetchLeadNotes(lead.id).then((data) => {
      setNotes(data);
      setLoadingNotes(false);
    });
  }, [lead.id]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    const note = await addLeadNote(lead.id, newNote.trim(), adminId);
    if (note) {
      setNotes([note, ...notes]);
      setNewNote('');
    }
    setAddingNote(false);
  };

  const cleanPhone = lead.phone?.replace(/[^0-9+]/g, '') || '';
  const whatsappLink = cleanPhone ? `https://wa.me/${cleanPhone.replace('+', '')}` : '';
  const statusColor = STATUS_COLORS[lead.status];

  const date = new Date(lead.created_at);
  const formattedDate = date.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const formattedTime = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{lead.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                lead.lead_type === 'contact' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
              }`}>
                {lead.lead_type === 'contact' ? 'Contact Lead' : 'Eligibility Lead'}
              </span>
              <span className="text-xs text-slate-400">{formattedDate} at {formattedTime}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoItem
              icon={Phone}
              label="Phone"
              value={
                <div className="flex items-center gap-2">
                  {lead.country_code && (
                    <span className="text-xs font-semibold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-md">{lead.country_code}</span>
                  )}
                  <a href={`tel:${cleanPhone}`} className="text-sm text-slate-900 hover:text-indigo-600">{lead.phone}</a>
                  {whatsappLink && (
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-600" title="WhatsApp">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
              }
            />
            <InfoItem icon={Mail} label="Email" value={
              <a href={`mailto:${lead.email}`} className="text-sm text-slate-900 hover:text-indigo-600">{lead.email}</a>
            } />
            {lead.country && <InfoItem icon={MapPin} label="Country" value={<span className="text-sm text-slate-900">{lead.country}</span>} />}
            {lead.source_page && <InfoItem icon={Globe} label="Source Page" value={<span className="text-sm text-slate-900">{lead.source_page}</span>} />}
            {lead.source_form && <InfoItem icon={Tag} label="Source Form" value={<span className="text-sm text-slate-900">{lead.source_form}</span>} />}
            {lead.button_name && <InfoItem icon={Tag} label="Submit Button" value={<span className="text-sm text-slate-900">{lead.button_name}</span>} />}
            {lead.cta_name && <InfoItem icon={Tag} label="Came From Button" value={<span className="text-sm text-slate-900">{lead.cta_name}{lead.cta_page ? ` (${lead.cta_page})` : ''}</span>} />}
            {lead.message && <div className="sm:col-span-2"><InfoItem icon={FileText} label="Message" value={<p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.message}</p>} /></div>}
          </div>

          {/* Score & Recommendation */}
          {(lead.score != null || lead.visa_recommendation) && (
            <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100">
              <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3">Eligibility Results</h3>
              <div className="flex items-center gap-6">
                {lead.score != null && (
                  <div>
                    <span className="text-xs text-slate-500">Score</span>
                    <div className={`text-2xl font-bold ${lead.score >= 65 ? 'text-green-600' : 'text-red-500'}`}>{lead.score}</div>
                  </div>
                )}
                {lead.visa_recommendation && (
                  <div>
                    <span className="text-xs text-slate-500">Recommendation</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Award className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-medium text-slate-900">{lead.visa_recommendation}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Status</h3>
            <div className="flex flex-wrap gap-2">
              {LEAD_STATUSES.map((s) => {
                const colors = STATUS_COLORS[s];
                const isActive = lead.status === s;
                return (
                  <button
                    key={s}
                    onClick={() => onStatusChange(s)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      isActive
                        ? `${colors.bg} ${colors.text} border-current`
                        : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isActive ? colors.dot : 'bg-slate-300'}`} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Remark & Called Date */}
          {(lead.remark || lead.called_date) && (
            <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
              <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Call Remark</h3>
              {lead.called_date && (
                <p className="text-xs text-slate-500 mb-1">
                  Called on: {new Date(lead.called_date).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              {lead.remark && (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.remark}</p>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Notes</h3>

            {/* Add Note */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                placeholder="Add a note..."
                className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500"
                disabled={addingNote}
              />
              <button
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="h-9 px-3 rounded-lg bg-indigo-600 text-white text-sm font-medium flex items-center gap-1.5 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                Add
              </button>
            </div>

            {/* Notes list */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {loadingNotes ? (
                <p className="text-sm text-slate-400">Loading notes...</p>
              ) : notes.length === 0 ? (
                <p className="text-sm text-slate-400">No notes yet.</p>
              ) : (
                notes.map((note) => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-700">{note.note}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-400">
                        {new Date(note.created_at).toLocaleDateString('en-AU', {
                          day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value }: { icon: any; label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      {value}
    </div>
  );
}
